import classnames from 'classnames';
import { useLayoutEffect, useRef } from 'react';
import { TOOLTIP_CURSOR_OFFSET } from '../../constants/SitePerformanceChart.constants';
import { setTooltipMotionTarget, stopTooltipMotion } from '../../utils/SitePerformanceChartTooltipAnimation';
import { SitePerformanceChartTooltip } from './SitePerformanceChartTooltip';
import { SitePerformanceChartTooltipOverlayProps } from './SitePerformanceChartTooltip.types';
import { SitePerformanceChartTooltipGlobalStyles } from './SitePerformanceChartTooltip.global.styles';

export const SitePerformanceChartTooltipOverlay = ({
  visible,
  cursorX,
  cursorY,
  containerWidth,
  containerHeight,
  siteIndex,
  ...tooltipProps
}: SitePerformanceChartTooltipOverlayProps): JSX.Element | null => {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const wasVisibleRef = useRef(false);

  useLayoutEffect((): (() => void) | void => {
    const wrapperEl = wrapperRef.current;

    if (!wrapperEl || !visible) {
      if (wrapperEl) {
        stopTooltipMotion(wrapperEl);
      }

      wasVisibleRef.current = false;
      return undefined;
    }

    let leftPx = cursorX + TOOLTIP_CURSOR_OFFSET;
    let topPx = cursorY + TOOLTIP_CURSOR_OFFSET;
    const tooltipWidth = wrapperEl.offsetWidth;
    const tooltipHeight = wrapperEl.offsetHeight;
    const overflowRight = leftPx + tooltipWidth - containerWidth;
    const overflowBottom = topPx + tooltipHeight - containerHeight;

    if (overflowRight > 0) {
      leftPx = Math.max(TOOLTIP_CURSOR_OFFSET, cursorX - overflowRight - TOOLTIP_CURSOR_OFFSET);
    }

    if (overflowBottom > 0) {
      topPx = Math.max(TOOLTIP_CURSOR_OFFSET, cursorY - overflowBottom - TOOLTIP_CURSOR_OFFSET);
    }

    const isFirstShow = !wasVisibleRef.current;

    setTooltipMotionTarget(wrapperEl, leftPx, topPx, isFirstShow);
    wasVisibleRef.current = true;

    return (): void => {
      stopTooltipMotion(wrapperEl);
    };
  }, [visible, cursorX, cursorY, containerWidth, containerHeight, siteIndex]);

  if (!visible) {
    return null;
  }

  return (
    <>
      <SitePerformanceChartTooltipGlobalStyles />

      <div
        ref={wrapperRef}
        className={classnames('site-performance-chart__tooltip-positioner', 'site-performance-chart__tooltip--visible')}
        style={{ zIndex: 2 }}
      >
        <SitePerformanceChartTooltip siteIndex={siteIndex} {...tooltipProps} />
      </div>
    </>
  );
};
